import { memo, useEffect } from 'react'
import { useRouter } from 'next/router'
import Head from 'next/head'

import { initializeTagTrackingManager } from 'nexchange-service/module/seo'

import config from '@/config'

const { DOMAIN } = config

interface MetadataManagerProps {
    title: string
    description: string
    fullTitle?: boolean
    image?: string
    type?: string
    keywords?: string
    canonical?: string
}

const MetadataManager = ({
    title,
    description,
    fullTitle = false,
    image = 'https://easywallet.io/images/logo/icons/logo-easywallet.svg',
    type = 'website',
    keywords = 'cryptocurrency wallet, bitcoin wallet, ethereum wallet, crypto exchange',
    canonical,
}: MetadataManagerProps) => {
    const finalTitle = fullTitle ? title : `${title} - EasyWallet`
    const { locales, asPath, locale } = useRouter()
    const currentUrl = `${DOMAIN}${asPath}`

    useEffect(() => {
        initializeTagTrackingManager()
    }, [])

    return (
        <Head>
            {/* Basic Meta Tags */}
            <meta charSet="utf-8" />
            <meta name="viewport" content="initial-scale=1.0, width=device-width" />
            <title>{finalTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <link rel="canonical" href={canonical || currentUrl} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={image} />
            <meta property="og:url" content={currentUrl} />
            <meta property="og:site_name" content="EasyWallet" />
            <meta property="og:locale" content={locale} />

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content="@easywallet" />

            {/* Favicon and App Icons */}
            <link rel="icon" sizes="180x180" href="/images/logo/icons/logo-easywallet.svg" />
            <link
                rel="icon"
                type="image/svg"
                sizes="32x32"
                href="/images/logo/icons/logo-easywallet.svg"
            />
            <link
                rel="icon"
                type="image/svg"
                sizes="16x16"
                href="/images/logo/icons/logo-easywallet.svg"
            />
            <link rel="manifest" href="/manifest.json" />
            <link rel="mask-icon" href="/images/logo/icons/logo-easywallet.svg" color="#6b73b1" />

            {/* PWA Meta Tags */}
            <meta name="application-name" content="EasyWallet" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-status-bar-style" content="default" />
            <meta name="apple-mobile-web-app-title" content="EasyWallet" />
            <meta name="msapplication-TileColor" content="#6b73b1" />
            <meta name="theme-color" content="#6b73b1" />

            {/* Alternate Language Links */}
            {locales &&
                locales.map(lang => (
                    <link
                        key={lang}
                        rel="alternate"
                        hrefLang={lang}
                        href={`${DOMAIN}/${lang}${asPath !== '/' ? asPath : ''}`}
                    />
                ))}
        </Head>
    )
}

export default memo(MetadataManager)
